import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { HeaderLinksDataProps } from '../Header';
import { notActiveLinkIdleColor } from '../header.utils';
import { VrLineHeight } from '../../../../../theming/verticalRhythm/verticalRhythm';
import { getGeneratedGlobalStyles } from '../../../../../typography/globalTypographyStyles';
import { useTheme } from '../../../../../theming/useTheme';
import { MoreLinks } from './MoreLinks';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../../../partials/link/TextLink';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';

const componentConfigThemeKey = 'secondaryLinks';

export interface SecondaryLinksThemeProps {
  vrLineHeight: VrLineHeight
  hoverOtherLinksColors: string
  idleColor: string
  activeStateColor: string
  hoverStateColor: string
}

interface state {
  isHoveredLink: boolean
}

interface SecondaryLinksLogicProps {
  maxVisibleLinks?: number
}

interface SecondaryLinksDataProps {
  moreLinksText: string
  links: HeaderLinksDataProps[]
    pagePath: string
}

export type SecondaryLinksProps = SecondaryLinksDataProps & SecondaryLinksLogicProps;

export type SecondaryLinksComponentProps = SecondaryLinksProps & SecondaryLinksThemeProps;

const getTextStyle = (vrLineHeight: VrLineHeight, isLastLink: boolean) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: isLastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px` : 0,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: isLastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px` : 0,
      },
    },
  });
};

const getContainerStyle = (vrLineHeight: VrLineHeight) => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        overflowX: 'auto',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        overflowX: 'auto',
      },
    },
    dropDownMenuWrapper: {
      height: '100%',
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'block',
      },
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'block',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none',
      },
    },
  });
};
export const isLastLink = (index: number, linksLength: number) => index + 1 === linksLength;

export const SecondaryLinksComponent:React.FunctionComponent<SecondaryLinksComponentProps> = props => {
  const createLink = ({ idleColor, activeStateColor, hoverStateColor, vrLineHeight, hoverOtherLinksColors }: SecondaryLinksComponentProps, linksLength: number, isHoveredLink: boolean, pagePath:string) => (
    (linkData: HeaderLinksDataProps, index: number) => {
      const isActiveLink = linkData.href === pagePath;
      const { maxVisibleLinks = 6 } = props;
      const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
      const calculatedHoverColor = isActiveLink ? activeStateColor : hoverStateColor;

      const linkStyleBasedOnScreen = linksLength > maxVisibleLinks && index >= maxVisibleLinks - 1
        ? {
          [MEDIA_BREAKPOINTS.LARGE]: {
            display: 'none',
          },
          [MEDIA_BREAKPOINTS.X_LARGE]: {
            display: 'none',
          },
          [MEDIA_BREAKPOINTS.SMALL]: {
            display: 'inline-flex',
          },
          [MEDIA_BREAKPOINTS.MEDIUM]: {
            display: 'inline-flex',
          },
        }
        : {};

      const linkStyle = generateLinkStyleFromTheme({ idleColor: calculatedIdleColor, activeStateColor, hoverStateColor: calculatedHoverColor }, linkStyleBasedOnScreen);
      const style = getTextStyle(vrLineHeight, isLastLink(index, linksLength));
      const contentStyle = StyleSheet.create(isActiveLink ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());

      return (
        <TextLinkComponent key={index} href={linkData.href} linkStyle={linkStyle}>
          <span className={css(contentStyle.style, style.link)}>{linkData.text}</span>
        </TextLinkComponent>
      );
    }
  );

  const { links, vrLineHeight, moreLinksText, maxVisibleLinks = 6, pagePath } = props;
  const [isHoveredLink, setIsHoveredLink] = React.useState(false);
  const style = getContainerStyle(vrLineHeight);
  const moreLinks = links.length > maxVisibleLinks ? links.slice(maxVisibleLinks - 1) : [];

  return (
    <div className={css(style.container)}>
      <div className={css(style.container)} onMouseEnter={() => setIsHoveredLink(true)} onMouseLeave={() => setIsHoveredLink(false)}>
        {links.map(createLink(props, links.length, isHoveredLink, pagePath))}
      </div>
      <div className={css(style.dropDownMenuWrapper)}>
        {moreLinks.length > 0 && <MoreLinks moreLinksText={moreLinksText} links={moreLinks} pagePath={pagePath} />}
      </div>
    </div>
  );
};

const mapThemeProps = (theme: SecondaryLinksThemeProps) => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    vrLineHeight: theme.vrLineHeight,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
  };
};

export const SecondaryLinks = (props: SecondaryLinksProps) => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps);
  return <SecondaryLinksComponent {...themeContext} {...props} />;
};
